import { useMemo } from "react";
import { useCurrentReturnData, useReturnData } from "@repo/api";

export function usePortfolioConstituents() {
  /**
   * Custom hook that combines return data and current return data into a single aggregated object
   * @param universe - The universe parameter
   * @param period - The period parameter
   * @param indicator - The indicator parameter
   * @param config - Optional API request configuration
   * @returns Object containing combined data, loading states, and error states
   */

  const returnData = useReturnData();
  const currentReturnsData = useCurrentReturnData();

  // Combine the data using the same logic as in the component
  const aggregatedData = useMemo(() => {
    if (currentReturnsData.data && returnData.data) {
      return {
        ...currentReturnsData.data,
        ...returnData.data,
      };
    } else {
      return {};
    }
  }, [returnData.data, currentReturnsData.data]);

  // Helper function to detect empty data
  const isEmptyData = (data: unknown): boolean => {
    if (!data) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    if (typeof data === "object" && Object.keys(data).length === 0) return true;
    return false;
  };

  const isCurrentDataEmpty = isEmptyData(currentReturnsData.data);
  const isReturnDataEmpty = isEmptyData(returnData.data);

  return {
    data: aggregatedData,
    isLoading:
      returnData.isLoading ||
      currentReturnsData.isLoading ||
      isCurrentDataEmpty ||
      isReturnDataEmpty,
    isError: returnData.isError || currentReturnsData.isError,
    error: returnData.error || currentReturnsData.error,
    refetch: () => {
      returnData.refetch();
      currentReturnsData.refetch();
    },
  };
}
